import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import LessonCard from "../components/LessonCard";
import { XMarkIcon } from "@heroicons/react/24/outline";
import BackButton from "../components/BackButton";
import { isSuperAdmin } from "../utils/users";

function Lessons({ userType = "user" }) {
  const [lessons, setLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [completedLessons, setCompletedLessons] = useState(new Set());

  // Fetch user's completed lessons from user_activity collection
  useEffect(() => {
    const fetchCompletedLessons = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          return;
        }

        const activityRef = collection(db, "user_activity");
        const q = query(
          activityRef,
          where("user_id", "==", doc(db, "users", currentUser.uid)),
          where("type", "==", "completed_lesson")
        );
        const querySnapshot = await getDocs(q);

        const completedIds = new Set();
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.lesson_id) {
            completedIds.add(data.lesson_id);
          }
        });

        console.log("Found completed lesson IDs:", Array.from(completedIds));

        setCompletedLessons(completedIds);
      } catch (error) {
        console.error("Error fetching completed lessons:", error);
        // Don't set error state - completion tracking failure shouldn't prevent lessons from loading
      }
    };

    fetchCompletedLessons();
  }, []);

  // Fetch lessons from Firestore
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const lessonsRef = collection(db, "node_lessons");
        const q = query(lessonsRef, orderBy("title"));
        const querySnapshot = await getDocs(q);

        const lessonsData = [];
        querySnapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        // Filter lessons based on user type
        // Super admins can see all lessons (including preview), others only see published lessons
        const filteredLessonsData = isSuperAdmin(userType)
          ? lessonsData
          : lessonsData.filter((lesson) => lesson.isPreview !== true);

        setLessons(filteredLessonsData);
        setFilteredLessons(filteredLessonsData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching lessons:", error);
        setError("Failed to load lessons");
        setLoading(false);
      }
    };

    fetchLessons();
  }, [userType]);

  // Filter lessons based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredLessons(lessons);
    } else {
      const filtered = lessons.filter((lesson) =>
        lesson.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredLessons(filtered);
    }
  }, [searchTerm, lessons]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Lessons Section */}
      <div className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Navigation */}
          <BackButton to="/" />

          {/* Header with Title and Search */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
            <h1 className="text-3xl font-bold">Lessons</h1>

            {/* Search Bar */}
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full sm:w-64 px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <p className="mt-2 text-gray-600">Loading lessons...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {!loading && !error && filteredLessons.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">
                {searchTerm
                  ? "No lessons found matching your search."
                  : "No lessons found."}
              </p>
            </div>
          )}

          {!loading && !error && filteredLessons.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min">
              {filteredLessons.map((lesson) => (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  isCompleted={completedLessons.has(lesson.id)}
                  returnPath="/lessons"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Lessons;
